/**
 * What a building does to the array, as a function of where the sun is.
 *
 * The row-shading model is analytic: infinite parallel rows have a closed-form
 * shaded fraction. An arbitrary lump of geometry has no such formula, so this
 * measures it instead - sample points across the module surfaces, a ray from
 * each towards the sun, and count how many are blocked.
 *
 * The result is stored as a TABLE OVER SUN POSITION rather than over time, for
 * three reasons: it is what the geometry actually depends on (the sun does not
 * care what day it is, only where it is); the same table then serves every hour
 * of every year without recomputation; and it is small enough - about 1500
 * numbers - to live in the project config and be applied by the backend without
 * shipping the mesh anywhere. This is the same shape as PVsyst's shading factor
 * table, for the same reasons.
 *
 * Everything here is pure and framework-free so it can run in a worker and be
 * tested in Node against cases with known answers.
 */

import { MAX_PACKED_COUNT, MODULE_SHADING_CODEC, packCounts } from "./moduleShadingCodec";

const DEG = Math.PI / 180;

export const TABLE_KIND = "obstacle_shading_table_v1";

/** Unit vector towards the sun, in (east, north, up). */
export function sunDirection(zenithDeg, azimuthDeg) {
  const sz = Math.sin(zenithDeg * DEG);
  return {
    x: sz * Math.sin(azimuthDeg * DEG),
    y: sz * Math.cos(azimuthDeg * DEG),
    z: Math.cos(zenithDeg * DEG),
  };
}

/**
 * Sample points spread evenly over the module surfaces.
 *
 * Cell centres, not corners: a corner grid puts half its samples on the
 * boundary, where a shadow edge is ambiguous, and systematically biases the
 * count. Each sample stands for an equal share of module area, so the blocked
 * fraction is a straight average.
 */
export function buildSampleGrid(rows, { across = 4, up = 8 } = {}) {
  const samples = [];
  rows.forEach((row, rowIndex) => {
    for (let i = 0; i < across; i += 1) {
      const fa = (i + 0.5) / across;
      for (let j = 0; j < up; j += 1) {
        const fu = (j + 0.5) / up;
        // Bilinear across the quad: low edge to high edge, end to end.
        const lo = {
          x: row.low[0].x + (row.low[1].x - row.low[0].x) * fa,
          y: row.low[0].y + (row.low[1].y - row.low[0].y) * fa,
          z: row.low[0].z + (row.low[1].z - row.low[0].z) * fa,
        };
        const hi = {
          x: row.high[0].x + (row.high[1].x - row.high[0].x) * fa,
          y: row.high[0].y + (row.high[1].y - row.high[0].y) * fa,
          z: row.high[0].z + (row.high[1].z - row.high[0].z) * fa,
        };
        samples.push({
          x: lo.x + (hi.x - lo.x) * fu,
          y: lo.y + (hi.y - lo.y) * fu,
          z: lo.z + (hi.z - lo.z) * fu,
          row: rowIndex,
          // Height up the row, so a caller can tell "the bottom edge is out"
          // from "the whole row is out" - the difference a diode cares about.
          heightFraction: fu,
        });
      }
    }
  });
  return samples;
}

/** An orthonormal frame with `w` along the sun. */
function sunFrame(dir) {
  const w = dir;
  // Any vector not parallel to w. Vertical fails only when the sun is at the
  // zenith, which happens, so fall back to north there.
  const ref = Math.abs(w.z) > 0.99 ? { x: 0, y: 1, z: 0 } : { x: 0, y: 0, z: 1 };
  let ux = ref.y * w.z - ref.z * w.y;
  let uy = ref.z * w.x - ref.x * w.z;
  let uz = ref.x * w.y - ref.y * w.x;
  const ul = Math.hypot(ux, uy, uz) || 1;
  ux /= ul; uy /= ul; uz /= ul;
  const vx = w.y * uz - w.z * uy;
  const vy = w.z * ux - w.x * uz;
  const vz = w.x * uy - w.y * ux;
  return { u: { x: ux, y: uy, z: uz }, v: { x: vx, y: vy, z: vz }, w };
}

/**
 * Fraction of the sampled area the mesh blocks, for one sun direction.
 *
 * Works in a frame aligned with the sun, where "does this triangle block that
 * point" collapses to a 2D point-in-triangle test plus a depth comparison. That
 * is what makes this affordable: the mesh is transformed once per direction
 * rather than once per ray, and a triangle whose 2D bounding box misses the
 * samples is rejected in four comparisons.
 *
 * @returns {{overall: number, perRow: number[]}}
 */
export function blockedFraction(triangles, samples, dir, rowCount) {
  const n = samples.length;
  const blocked = new Uint8Array(n);
  if (!triangles || triangles.length === 0 || n === 0) {
    return { overall: 0, perRow: new Array(rowCount || 0).fill(0), blocked };
  }

  const { u, v, w } = sunFrame(dir);

  // Samples in the sun frame, plus the region they occupy.
  const su = new Float64Array(n);
  const sv = new Float64Array(n);
  const sw = new Float64Array(n);
  let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity, minW = Infinity;
  for (let i = 0; i < n; i += 1) {
    const p = samples[i];
    su[i] = p.x * u.x + p.y * u.y + p.z * u.z;
    sv[i] = p.x * v.x + p.y * v.y + p.z * v.z;
    sw[i] = p.x * w.x + p.y * w.y + p.z * w.z;
    if (su[i] < minU) minU = su[i];
    if (su[i] > maxU) maxU = su[i];
    if (sv[i] < minV) minV = sv[i];
    if (sv[i] > maxV) maxV = sv[i];
    if (sw[i] < minW) minW = sw[i];
  }

  // The samples binned on a grid over that region, so a triangle is tested
  // against the samples under its own footprint rather than against all of
  // them. A field's own rows are most of the mesh and each covers a few
  // modules, so testing every sample against every triangle made the work
  // grow with modules squared: a few seconds at 500 modules, minutes at a
  // few thousand. About four samples to a cell.
  const spanU = Math.max(maxU - minU, 1e-9);
  const spanV = Math.max(maxV - minV, 1e-9);
  const cell = Math.sqrt((spanU * spanV) / Math.max(1, n / 4)) || 1;
  const nu = Math.max(1, Math.min(2048, Math.ceil(spanU / cell)));
  const nv = Math.max(1, Math.min(2048, Math.ceil(spanV / cell)));
  const cu0 = nu / spanU;
  const cv0 = nv / spanV;
  const cellOf = new Int32Array(n);
  const start = new Int32Array(nu * nv + 1);
  for (let i = 0; i < n; i += 1) {
    const iu = Math.min(nu - 1, Math.floor((su[i] - minU) * cu0));
    const iv = Math.min(nv - 1, Math.floor((sv[i] - minV) * cv0));
    cellOf[i] = iv * nu + iu;
    start[cellOf[i] + 1] += 1;
  }
  for (let c = 0; c < nu * nv; c += 1) start[c + 1] += start[c];
  const fill = start.slice(0, nu * nv);
  const order = new Int32Array(n);
  for (let i = 0; i < n; i += 1) order[fill[cellOf[i]]++] = i;

  for (let t = 0; t < triangles.length; t += 9) {
    // Project the triangle into the sun frame.
    const au = triangles[t] * u.x + triangles[t + 1] * u.y + triangles[t + 2] * u.z;
    const av = triangles[t] * v.x + triangles[t + 1] * v.y + triangles[t + 2] * v.z;
    const aw = triangles[t] * w.x + triangles[t + 1] * w.y + triangles[t + 2] * w.z;
    const bu = triangles[t + 3] * u.x + triangles[t + 4] * u.y + triangles[t + 5] * u.z;
    const bv = triangles[t + 3] * v.x + triangles[t + 4] * v.y + triangles[t + 5] * v.z;
    const bw = triangles[t + 3] * w.x + triangles[t + 4] * w.y + triangles[t + 5] * w.z;
    const cu = triangles[t + 6] * u.x + triangles[t + 7] * u.y + triangles[t + 8] * u.z;
    const cv = triangles[t + 6] * v.x + triangles[t + 7] * v.y + triangles[t + 8] * v.z;
    const cw = triangles[t + 6] * w.x + triangles[t + 7] * w.y + triangles[t + 8] * w.z;

    // Reject anything that cannot cover a sample, and anything entirely behind
    // every sample (a triangle further from the sun than the module casts no
    // shadow on it).
    const loU = Math.min(au, bu, cu), hiU = Math.max(au, bu, cu);
    const loV = Math.min(av, bv, cv), hiV = Math.max(av, bv, cv);
    if (loU > maxU || hiU < minU) continue;
    if (loV > maxV || hiV < minV) continue;
    if (Math.max(aw, bw, cw) <= minW + 1e-3) continue;

    const d = (bv - cv) * (au - cu) + (cu - bu) * (av - cv);
    if (Math.abs(d) < 1e-12) continue;                  // edge-on: no area, no shadow
    const inv = 1 / d;

    // The grid cells under the triangle's footprint, by the same arithmetic
    // that binned the samples, so a sample inside the footprint is always in
    // one of them.
    const i0 = Math.max(0, Math.min(nu - 1, Math.floor((loU - minU) * cu0)));
    const i1 = Math.max(0, Math.min(nu - 1, Math.floor((hiU - minU) * cu0)));
    const j0 = Math.max(0, Math.min(nv - 1, Math.floor((loV - minV) * cv0)));
    const j1 = Math.max(0, Math.min(nv - 1, Math.floor((hiV - minV) * cv0)));
    for (let j = j0; j <= j1; j += 1) {
      const rowBase = j * nu;
      for (let c = rowBase + i0; c <= rowBase + i1; c += 1) {
        for (let k = start[c], kEnd = start[c + 1]; k < kEnd; k += 1) {
          const i = order[k];
          if (blocked[i]) continue;
          const pu = su[i];
          const pv = sv[i];
          // Barycentric containment.
          const l1 = ((bv - cv) * (pu - cu) + (cu - bu) * (pv - cv)) * inv;
          if (l1 < 0 || l1 > 1) continue;
          const l2 = ((cv - av) * (pu - cu) + (au - cu) * (pv - cv)) * inv;
          if (l2 < 0 || l2 > 1) continue;
          const l3 = 1 - l1 - l2;
          if (l3 < 0 || l3 > 1) continue;
          // Depth of the triangle at that point, along the sun direction. Only
          // a surface BETWEEN the sample and the sun blocks it; 1 mm of
          // tolerance keeps a mesh that touches the array from shadowing itself.
          const depth = l1 * aw + l2 * bw + l3 * cw;
          if (depth > sw[i] + 1e-3) blocked[i] = 1;
        }
      }
    }
  }

  const rows = rowCount || 0;
  const perRowHits = new Array(rows).fill(0);
  const perRowTotal = new Array(rows).fill(0);
  let hits = 0;
  for (let i = 0; i < n; i += 1) {
    if (samples[i].row < rows) {
      perRowTotal[samples[i].row] += 1;
      if (blocked[i]) perRowHits[samples[i].row] += 1;
    }
    if (blocked[i]) hits += 1;
  }
  return {
    blocked,
    overall: hits / n,
    perRow: perRowHits.map((h, i) => (perRowTotal[i] ? h / perRowTotal[i] : 0)),
  };
}

/** The sun's largest declination, plus a margin for refraction and the margin. */
const MAX_DECLINATION = 23.9;

/**
 * Can the sun ever be at this azimuth and elevation, from this latitude?
 *
 * sin(declination) = sin(lat) sin(elevation) + cos(lat) cos(elevation) cos(azimuth)
 *
 * Everything outside +-23.9 deg of declination is sky the sun never visits, and
 * at mid latitudes that is more than half the hemisphere - due north at 40 deg
 * elevation from Greece, for instance. Not computing those cells is the single
 * biggest saving here, and it costs one cosine per cell to know.
 */
function reachable(latitude, azimuthDeg, elevationDeg) {
  const phi = latitude * DEG;
  const e = elevationDeg * DEG;
  const a = azimuthDeg * DEG;
  const sinDec =
    Math.sin(phi) * Math.sin(e) + Math.cos(phi) * Math.cos(e) * Math.cos(a);
  if (sinDec < -1 || sinDec > 1) return false;
  return Math.abs(Math.asin(sinDec) / DEG) <= MAX_DECLINATION;
}

/**
 * Drop triangles that cannot shade anything, before the expensive part.
 *
 * A surface entirely below the lowest module can only block the beam when the
 * sun is below the horizon, where there is no beam. Terrain and ground planes
 * are usually most of an exported scene's triangle count, so this is often the
 * difference between seconds and a minute.
 */
function cullBelow(triangles, minZ) {
  const keep = [];
  for (let t = 0; t < triangles.length; t += 9) {
    if (
      triangles[t + 2] > minZ ||
      triangles[t + 5] > minZ ||
      triangles[t + 8] > minZ
    ) {
      for (let k = 0; k < 9; k += 1) keep.push(triangles[t + k]);
    }
  }
  return Float32Array.from(keep);
}

/**
 * Build the sun-position table.
 *
 * Only the part of the sky the sun can actually reach at this latitude is
 * computed; the rest is stored as -1 ("never happens") rather than 0, so a
 * reader can tell "not shaded" from "not asked". `onProgress` is called with a
 * 0..1 fraction so a UI can show something during the seconds this takes, and
 * `shouldCancel` is polled so it can be abandoned.
 *
 * The default 10 deg x 5 deg grid is a deliberate trade: the shading factor
 * varies smoothly with sun position except at silhouette edges, the lookup
 * interpolates between cells, and a finer grid multiplies the cost by four for
 * a difference well inside the sampling noise. Pass a finer step for a final
 * check on an awkward site.
 */
export function buildShadingTable(opts = {}) {
  const ctx = prepareTable(opts);
  for (let ai = 0; ai < ctx.azCount; ai += 1) {
    computeColumn(ctx, ai);
    if (opts.shouldCancel && opts.shouldCancel()) return null;
    if (opts.onProgress) opts.onProgress((ai + 1) / ctx.azCount);
  }
  return finishTable(ctx);
}

/**
 * The same table, computed without freezing the page.
 *
 * A few seconds of straight-line JavaScript blocks every event, so a progress
 * bar drawn from a synchronous loop never actually paints and a cancel button
 * never gets the click. Yielding between azimuth columns costs a millisecond or
 * two in total and makes both of them work. The layout's calculation runs this
 * inside a Web Worker (workers/scenePreparation.worker.js), where the yields
 * cost nothing; on the page it is the fallback where no worker is available.
 */
export async function buildShadingTableAsync(opts = {}) {
  const ctx = prepareTable(opts);
  for (let ai = 0; ai < ctx.azCount; ai += 1) {
    computeColumn(ctx, ai);
    if (opts.onProgress) opts.onProgress((ai + 1) / ctx.azCount);
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => setTimeout(resolve, 0));
    if (opts.shouldCancel && opts.shouldCancel()) return null;
  }
  return finishTable(ctx);
}

/**
 * Set up one table computation.
 *
 * Split from the loop so the same work can run straight through (in Node, in a
 * test) or column by column with yields (in a browser) without two copies of
 * the arithmetic drifting apart.
 *
 * The default 10 deg x 5 deg grid is a deliberate trade: the shading factor
 * varies smoothly with sun position except at silhouette edges, the lookup
 * interpolates between cells, and a finer grid multiplies the cost by four for
 * a difference well inside the sampling noise. Pass a finer step for a final
 * check on an awkward site.
 */
function prepareTable({
  triangles,
  rows,
  rowsFor = null,
  samplesFor = null,
  // Whole-plant mode: the modules were found in the imported mesh rather than
  // built from the layout, so the sample points arrive ready-made and there are
  // no row quads to build a grid from.
  explicitSamples = null,
  rowCount = null,
  latitude,
  azStart = 0,
  azStep = 10,
  azCount = 36,
  elStart = 0,
  elStep = 5,
  elCount = 19,
  samples: sampleOpts,
  moduleMetadata = null,
  includesRows = Boolean(explicitSamples),
} = {}) {
  const samples = explicitSamples || buildSampleGrid(rows, sampleOpts);
  const minSampleZ = rowsFor || samplesFor ? -Infinity : samples.reduce((m, p) => Math.min(m, p.z), Infinity);
  return {
    samples,
    rows,
    rowCount: rowCount != null ? rowCount : (rows ? rows.length : 0),
    includesRows,
    moduleMetadata,
    moduleValues: moduleMetadata?.map(() => new Float32Array(azCount * elCount).fill(-1)),
    // The same readings as counts of blocked sample points, which is what is
    // stored (utils/moduleShadingCodec), with each module's number of points.
    moduleHits: moduleMetadata?.map(() => new Uint16Array(azCount * elCount)),
    moduleTotals: moduleMetadata ? new Int32Array(moduleMetadata.length).fill(-1) : null,
    moduleTotalsVary: moduleMetadata ? new Uint8Array(moduleMetadata.length) : null,
    // A tracker's rows do not hold still: their rotation is a function of where
    // the sun is, which is exactly what this table is indexed by. When the
    // caller supplies rowsFor(azimuth, elevation) the collector geometry is
    // rebuilt for every cell, so the measurement is of the array as it will
    // actually be standing at that moment rather than at one frozen angle.
    rowsFor,
    samplesFor,
    sampleOpts,
    latitude,
    azStart, azStep, azCount,
    elStart, elStep, elCount,
    mesh: cullBelow(triangles || [], minSampleZ),
    given: triangles ? triangles.length / 9 : 0,
    values: new Float32Array(azCount * elCount).fill(-1),
    perRowMax: new Float32Array(azCount * elCount).fill(-1),
    computed: 0,
  };
}

/** One azimuth column of the table. */
function computeColumn(ctx, ai) {
  const az = ctx.azStart + ai * ctx.azStep;
  for (let ei = 0; ei < ctx.elCount; ei += 1) {
    const el = ctx.elStart + ei * ctx.elStep;
    const idx = ai * ctx.elCount + ei;
    if (el <= 0) {
      // At or below the horizon there is no beam to block.
      ctx.values[idx] = 0;
      ctx.perRowMax[idx] = 0;
      ctx.moduleValues?.forEach((v) => { v[idx] = 0; });
      continue;
    }
    if (!reachable(ctx.latitude, az, el)) continue;      // stays -1: never happens
    const samples = ctx.samplesFor ? ctx.samplesFor(az, el) : ctx.rowsFor
      ? buildSampleGrid(ctx.rowsFor(az, el), ctx.sampleOpts)
      : ctx.samples;
    const res = blockedFraction(
      ctx.mesh, samples, sunDirection(90 - el, az), ctx.rowCount
    );
    ctx.computed += 1;
    ctx.values[idx] = res.overall;
    ctx.perRowMax[idx] = Math.max(0, ...res.perRow);
    if (ctx.moduleValues) {
      const hits = new Uint32Array(ctx.moduleValues.length), totals = new Uint32Array(ctx.moduleValues.length);
      samples.forEach((sample, i) => { totals[sample.module] += 1; hits[sample.module] += res.blocked[i]; });
      ctx.moduleValues.forEach((values, i) => { values[idx] = totals[i] ? hits[i] / totals[i] : 0; });
      ctx.moduleHits.forEach((counts, i) => {
        counts[idx] = hits[i];
        if (ctx.moduleTotals[i] < 0) ctx.moduleTotals[i] = totals[i];
        else if (ctx.moduleTotals[i] !== totals[i]) ctx.moduleTotalsVary[i] = 1;
      });
    }
  }
}

function finishTable(ctx) {
  // The cells the sun can reach, where every module has a reading.
  const cells = [];
  ctx.values.forEach((value, i) => { if (value >= 0) cells.push(i); });
  return {
    ...(ctx.moduleMetadata ? {
      // Packed (utils/moduleShadingCodec): a module's readings are counts of
      // its blocked sample points. Spelled out only for a module whose count
      // a byte cannot hold or whose number of points changed between cells.
      moduleShading: ctx.moduleMetadata.map((m, i) => {
        const n = ctx.moduleTotals[i];
        if (n > 0 && n <= MAX_PACKED_COUNT && !ctx.moduleTotalsVary[i]) {
          return { ...m, n, packed: packCounts(cells.map((cell) => ctx.moduleHits[i][cell])) };
        }
        return { ...m, values: Array.from(ctx.moduleValues[i], (v) => v < 0 ? -1 : Math.round(v * 1000) / 1000) };
      }),
      moduleShadingCodec: MODULE_SHADING_CODEC,
      method: "module-area-raycast-v1",
      samplesPerModule: ctx.samples.length / ctx.moduleMetadata.length,
    } : {}),
    kind: TABLE_KIND,
    azStart: ctx.azStart, azStep: ctx.azStep, azCount: ctx.azCount,
    elStart: ctx.elStart, elStep: ctx.elStep, elCount: ctx.elCount,
    // Rounded on the way out: three decimals is a tenth of a percent of module
    // area, far below the resolution of the sampling itself, and it keeps the
    // stored table a few kilobytes rather than tens.
    values: Array.from(ctx.values, (v) => (v < 0 ? -1 : Math.round(v * 1000) / 1000)),
    perRowMax: Array.from(ctx.perRowMax, (v) => (v < 0 ? -1 : Math.round(v * 1000) / 1000)),
    samplesPerRow: ctx.samples.length / Math.max(1, ctx.rowCount),
    rows: ctx.rowCount,
    // Whether the row in front is already inside these numbers. When it is, the
    // engine must apply this table INSTEAD of the analytic row fraction, not on
    // top of it - the same shadow counted twice is a plant that looks worse
    // than it is, and nothing in the numbers would say why.
    includesRows: ctx.includesRows,
    // For the report: how much of the sky was actually measured, and how much
    // of the mesh survived the cull. Both are the first things to look at when
    // a table comes back suspiciously empty.
    cellsComputed: ctx.computed,
    cellsTotal: ctx.azCount * ctx.elCount,
    trianglesUsed: ctx.mesh.length / 9,
    trianglesGiven: ctx.given,
  };
}


/**
 * Read the table at a sun position, interpolating between bins.
 *
 * Bilinear, and cells marked "never happens" are treated as their nearest
 * computed neighbour rather than as zero - at the edge of the reachable sky the
 * alternative is a shadow that vanishes as the sun crosses a bin boundary.
 * The backend does the same thing; if you change this, change that too.
 */
export function lookupShadingTable(table, azimuthDeg, elevationDeg) {
  if (!table || !table.values || elevationDeg <= 0) return 0;
  const { azStart, azStep, azCount, elStart, elStep, elCount, values } = table;

  const az = ((((azimuthDeg - azStart) % 360) + 360) % 360) / azStep;
  const el = (elevationDeg - elStart) / elStep;
  const a0 = Math.floor(az);
  const e0 = Math.max(0, Math.min(elCount - 1, Math.floor(el)));
  const fa = az - a0;
  const fe = Math.max(0, Math.min(1, el - e0));

  const cell = (ai, ei) => {
    const a = ((ai % azCount) + azCount) % azCount;
    const e = Math.max(0, Math.min(elCount - 1, ei));
    const v = values[a * elCount + e];
    if (v >= 0) return v;
    // Walk down to the nearest computed elevation in this azimuth column.
    for (let k = e - 1; k >= 0; k -= 1) {
      const w = values[a * elCount + k];
      if (w >= 0) return w;
    }
    return 0;
  };

  const v00 = cell(a0, e0);
  const v10 = cell(a0 + 1, e0);
  const v01 = cell(a0, e0 + 1);
  const v11 = cell(a0 + 1, e0 + 1);
  const top = v00 * (1 - fa) + v10 * fa;
  const bottom = v01 * (1 - fa) + v11 * fa;
  return Math.max(0, Math.min(1, top * (1 - fe) + bottom * fe));
}
